import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Card,
  CardMedia,
  CardContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Skeleton } from '@mui/material';
import { SAMPLE_AVATARS, getAvatarImageUrl } from '../../utils/avatars';
import api from '../../apis/api';

const AvatarSelectionModal = ({
  open,
  onClose,
  onSelectAvatar,
  projectId,
  userDetails,
  showMessage,
  project
}) => {
  // Small helper component to render an image with a Skeleton placeholder
  const ImageCard = ({ src, alt, children }) => {
    const [loaded, setLoaded] = useState(false);
    const safeSrc = src || '/default-avatar.png';
    return (
      <Box sx={{ position: 'relative', width: '100%', minHeight: 350, display: 'block' }}>
        {!loaded && <Skeleton variant="rectangular" width="100%" height={350} />}
        <CardMedia
          component="img"
          image={safeSrc}
          alt={alt}
          sx={{ width: '100%', height: 350, objectFit: 'cover', display: loaded ? 'block' : 'none' }}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
        {children}
      </Box>
    );
  };
  const [avatarModalTab, setAvatarModalTab] = useState(0);
  const [heygenAvatars, setHeygenAvatars] = useState(SAMPLE_AVATARS);
  const [groupAvatars, setGroupAvatars] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [groupView, setGroupView] = useState(false);
  const [isFetchingGroupAvatars, setIsFetchingGroupAvatars] = useState(false);
  const [newAvatarPrompt, setNewAvatarPrompt] = useState('');
  const [generatingAvatar, setGeneratingAvatar] = useState(false);
  const [selectedAvatarForProject, setSelectedAvatarForProject] = useState(null);
  const [selectedAvatarGroup, setSelectedAvatarGroup] = useState(null);
  const [showGenerateInline, setShowGenerateInline] = useState(false);
  const [generatedLooks, setGeneratedLooks] = useState([]);
  const [loadingGeneratedLooks, setLoadingGeneratedLooks] = useState(false);
  const [showGeneratedLooks, setShowGeneratedLooks] = useState(false);
  const [pollImages, setPollImages] = useState([]);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [lookName, setLookName] = useState('');
  const [pendingLook, setPendingLook] = useState(null);
  const [addingLook, setAddingLook] = useState(false);
  const [deletingLookKey, setDeletingLookKey] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingGroupAvatarId, setDeletingGroupAvatarId] = useState(null);
  const [showDeleteGroupDialog, setShowDeleteGroupDialog] = useState(false);
  const [pendingGroupDelete, setPendingGroupDelete] = useState(null);
  const pollTimerRef = useRef(null);
  const pollingGenerationRef = useRef(null);
  const inputRef = useRef(null);

  const isProcessingLikeStatus = (status) => {
    const st = (status == null ? '' : String(status)).trim().toLowerCase();
    return ['processing', 'in_progress', 'in progress', 'pending', 'queued', 'created'].includes(st);
  };

  const getOrgId = () => (
    project?.orgId?._id ||
    project?.orgId ||
    project?.org_id ||
    project?.organizationId ||
    project?.organization_id ||
    (project?.organization && project.organization.id) ||
    userDetails?.orgId ||
    userDetails?.org_id ||
    null
  );

  const handleSoftDeleteGeneratedLook = async ({ generationId, imageKeyOrUrl }) => {
    if (!generationId || !imageKeyOrUrl) {
      showMessage('Unable to delete: missing generationId or image', 'error');
      return;
    }

    const orgId = getOrgId();
    const fullUrl = imageKeyOrUrl
      ? (String(imageKeyOrUrl).startsWith('http') ? String(imageKeyOrUrl) : `https://resource2.heygen.ai/${imageKeyOrUrl}`)
      : null;

    setDeletingLookKey(imageKeyOrUrl);
    try {
      await api.post('/heygen/photo-avatar/generation/looks/delete', {
        generationId,
        image_url: fullUrl,
        orgId
      }, {
        headers: { 'x-access-token': userDetails?.jwtToken || '' }
      });

      // Update UI immediately: remove this key from any generatedLooks items
      setGeneratedLooks((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const next = list
          .map((item) => {
            const itemGen = item?.generationId || item?.generation_id;
            if (String(itemGen) !== String(generationId)) return item;
            const keys = Array.isArray(item?.image_key_list) ? item.image_key_list : [];
            const urls = Array.isArray(item?.image_url_list) ? item.image_url_list : [];

            return {
              ...item,
              image_key_list: keys.filter((k) => String(k) !== String(imageKeyOrUrl)),
              image_url_list: urls.filter((u) => String(u) !== String(imageKeyOrUrl))
            };
          })
          .filter((item) => {
            const keys = Array.isArray(item?.image_key_list) ? item.image_key_list : [];
            const urls = Array.isArray(item?.image_url_list) ? item.image_url_list : [];
            return keys.length > 0 || urls.length > 0;
          });
        return next;
      });

      // If user had this one selected, clear selection
      if (selectedAvatarForProject?.image_url === imageKeyOrUrl) {
        setSelectedAvatarForProject(null);
      }

      showMessage('Look deleted', 'success');
    } catch (e) {
      console.error('Failed to delete generated look:', e);
      showMessage(e?.response?.data?.message || 'Failed to delete look', 'error');
    } finally {
      setDeletingLookKey(null);
    }
  };

  useEffect(() => {
    if (open) {
      setHeygenAvatars(SAMPLE_AVATARS);
      setGroupAvatars([]);
      setGroupView(false);
      setSelectedAvatarForProject(null);
      setShowGenerateInline(false);
      setNewAvatarPrompt('');
    }
  }, [open]);

  useEffect(() => {
    if (showGenerateInline && inputRef.current) {
      try { inputRef.current.focus(); } catch (e) {}
    }
  }, [showGenerateInline]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      pollingGenerationRef.current = null;
    };
  }, []);

  const fetchHeygenAvatars = async (groupId = null, defaultAvatarSrc = null) => {
    setIsFetchingGroupAvatars(true);
    try {
      const orgId = getOrgId();
      const url = orgId
        ? `/heygen/avatars/group/${encodeURIComponent(groupId)}?orgId=${encodeURIComponent(orgId)}`
        : `/heygen/avatars/group/${encodeURIComponent(groupId)}`;

      const body = await api.get(url, {
        headers: { 'x-access-token': userDetails?.jwtToken || '' }
      });

      let list = [];
      if (body && body.data) {
        const heygenData = body.data.data || body.data;
        if (heygenData && heygenData.avatar_list) list = heygenData.avatar_list;
      }

      // Add the clicked/default avatar into the list so user can select it too.
      // Normalize to the same shape the group list expects (uses `id` + `preview_image_url`).
      const merged = Array.isArray(list) ? [...list] : [];
      const src = defaultAvatarSrc || selectedAvatarGroup;
      const defaultId = src?.avatar_id || src?.id || src?._id || null;
      const defaultGroupId = src?.group_id || src?.groupId || src?.id || groupId || null;

      if (defaultId && !merged.some((a) => String(a?.id) === String(defaultId))) {
        const img = getAvatarImageUrl(src) || src?.preview_image_url || src?.preview || null;
        merged.unshift({
          id: defaultId,
          avatar_id: defaultId,
          group_id: defaultGroupId,
          template_id: src?.template_id || src?.templateId || null,
          templateId: src?.templateId || src?.template_id || null,
          name: src?.avatar_name || src?.name || src?.display_name || 'Default Avatar',
          avatar_name: src?.avatar_name || src?.name || src?.display_name || 'Default Avatar',
          gender: src?.gender || '',
          preview_image_url: img,
          image_url: img,
          status: src?.status || 'ready',
          isDefault: true
        });
      }

      setGroupAvatars(merged);
      setGroupView(true);
      // Clear any generated-looks view or polled images when switching to a fresh group
      setShowGeneratedLooks(false);
      setPollImages([]);
      setSelectedAvatarForProject(null);
    } catch (error) {
      console.error('Failed to fetch HeyGen group avatars:', error);
      showMessage(error?.response?.data?.message || 'Failed to load avatars from HeyGen', 'error');
    } finally {
      setIsFetchingGroupAvatars(false);
    }
  };

  const handleDeleteGroupAvatar = async ({ photoAvatarId, groupId }) => {
    if (!photoAvatarId) {
      showMessage('Unable to delete: missing avatar id', 'error');
      return;
    }

    const orgId = getOrgId();
    const qs = [];
    if (orgId) qs.push(`orgId=${encodeURIComponent(orgId)}`);
    if (groupId) qs.push(`groupId=${encodeURIComponent(groupId)}`);
    const url = `/heygen/photo-avatar/${encodeURIComponent(photoAvatarId)}${qs.length ? `?${qs.join('&')}` : ''}`;

    setDeletingGroupAvatarId(photoAvatarId);
    try {
      await api.delete(url, {
        headers: { 'x-access-token': userDetails?.jwtToken || '' }
      });

      setGroupAvatars((prev) => (Array.isArray(prev) ? prev.filter((a) => String(a?.id) !== String(photoAvatarId)) : []));
      if (selectedAvatarForProject && String(selectedAvatarForProject?.id) === String(photoAvatarId)) {
        setSelectedAvatarForProject(null);
      }
      showMessage('Avatar deleted', 'success');
    } catch (e) {
      console.error('Failed to delete group avatar:', e);
      showMessage(e?.response?.data?.message || 'Failed to delete avatar', 'error');
    } finally {
      setDeletingGroupAvatarId(null);
    }
  };

  const extractGroupId = (avatar) => {
    if (!avatar) return null;
    return avatar.group_id || avatar.avatar_id || avatar.id || null;
  };

  const handleGenerateNewAvatarLook = async (groupId = null, templateId = null) => {
    const promptText = (newAvatarPrompt || '').trim();
    if (!promptText) {
      showMessage('enter a prompt to generate avatar', 'error');
      return;
    }

    setGeneratingAvatar(true);
    try {
      const orgId = getOrgId();
      const payload = { prompt: promptText };
      if (groupId) payload.groupId = groupId;
      if (templateId) payload.templateId = templateId;
      if (orgId) payload.orgId = orgId;

      const resp = await api.post('/heygen/photo-avatar/look/generate', payload, {
        headers: { 'x-access-token': userDetails?.jwtToken || '' }
      });

      showMessage('Avatar look generation started! Waiting for images...', 'success');
      // capture generation id from response
      const genId = resp?.data?.data?.generation_id || resp?.data?.generation_id || resp?.data?.data?.generationId || resp?.data?.generationId || resp?.data?.data?.id || resp?.data?.id || null;
      setNewAvatarPrompt('');

      if (genId) {
        // start polling every 10s
        setLoadingGeneratedLooks(true);
        pollingGenerationRef.current = genId;
        setPollImages([]);
        // setShowGeneratedLooks(true);
        // clear any existing timer
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        pollTimerRef.current = setInterval(async () => {
          try {
            // include orgId when polling generation status so server can scope results
            const pollOrgParam = orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
            const statusResp = await api.get(`/heygen/photo-avatar/generation/${encodeURIComponent(genId)}${pollOrgParam}`, {
              headers: { 'x-access-token': userDetails?.jwtToken || '' }
            });
            console.log('Polled avatar generation status:', statusResp);
            const payload = statusResp?.data?.data || statusResp?.data || statusResp;
            // try to extract image urls
            const imagesCandidate = payload?.image_url_list || payload?.data?.image_list || payload?.data?.outputs || payload?.data?.result || payload?.images || payload?.outputs || payload?.image_list;
            let urls = payload?.image_url_list || [];
            if (Array.isArray(imagesCandidate) && imagesCandidate.length) {
              urls = imagesCandidate.map(i => (typeof i === 'string' ? i : (i?.url || i?.image_url || i?.download_url || i?.path || i?.output || null))).filter(Boolean);
            }
            if (urls.length) {
              // populate generatedLooks immediately so UI updates
              setGeneratedLooks([{ image_url_list: urls, image_key_list: payload?.image_key_list, generationId: genId, prompt: promptText }]);
              setPollImages([]);
              setLoadingGeneratedLooks(false);
              // stop showing generate button loading now that results are available
              setGeneratingAvatar(false);
              setShowGeneratedLooks(true);
              // stop polling
              if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
              // keep pollingGenerationRef.current set to genId for reference
            }
            // if payload indicates failure, stop polling
            const st = (payload?.data?.status || payload?.status || payload?.state || '').toString().toLowerCase();
            if (st && (st.includes('fail') || st.includes('error'))) {
              setLoadingGeneratedLooks(false);
              setGeneratingAvatar(false);
              if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
              showMessage('Avatar generation failed', 'error');
            }
          } catch (e) {
            // ignore transient errors while polling
            // optionally stop after many attempts (not implemented)
          }
        }, 10000);
      } else {
        // No generation id returned: fallback to refresh group avatars later
        if (groupId) setTimeout(() => fetchHeygenAvatars(groupId, selectedAvatarGroup), 3000);
      }
    } catch (error) {
      console.error('Failed to generate avatar look:', error);
      showMessage(error?.response?.data?.message || 'Failed to generate avatar look', 'error');
      setGeneratingAvatar(false);
    } finally {
      // keep the inline generate UI open so users can see progress/results
    }
  };

  const fetchGeneratedLooks = async () => {
    setLoadingGeneratedLooks(true);
    try {
      const groupId = extractGroupId(selectedAvatarGroup);
      console.log('project:', project);
      const orgId = getOrgId();
      let url = '/heygen/photo-avatar/generation/looks/list';
      if (groupId) url = `/heygen/photo-avatar/generation/looks/list?groupId=${encodeURIComponent(groupId)}`;
      if (orgId) url += (url.includes('?') ? '&' : '?') + `orgId=${encodeURIComponent(orgId)}`;
      const body = await api.get(url, {
        headers: { 'x-access-token': userDetails?.jwtToken || '' }
      });

      let list = [];
      if (body && body.data) {
        list = body.data.data || body.data;
      }

      // If user has a selected group, filter looks to that group for relevance
      // const groupId = extractGroupId(selectedAvatarGroup);
      const filtered = Array.isArray(list) ? (groupId ? list.filter(l => String(l.groupId) === String(groupId)) : list) : [];
      if (Array.isArray(filtered) && filtered.length === 0) {
        showMessage('No generated looks found', 'info');
      }
      setGeneratedLooks(filtered);
      setShowGeneratedLooks(true);
    } catch (error) {
      console.error('Failed to fetch generated looks:', error);
      showMessage(error?.response?.data?.message || 'Failed to load generated looks', 'error');
    } finally {
      setLoadingGeneratedLooks(false);
    }
  };

  const handleSelectAvatar = async () => {
    if (!selectedAvatarForProject) {
      showMessage('Please select an avatar', 'error');
      return;
    }

    const src = selectedAvatarForProject;
    const avatarId = src.avatar_id || src.id || src._id || src.character_id || null;

    try {
      let normalized = {};

      // if (src.image_url) {
      //   // Generated look: apply image_url to project
      //   await api.put(`/project/${projectId}/avatar`, { image_url: src.image_url, generationId: src.generationId || null }, {
      //     headers: { 'x-access-token': userDetails?.jwtToken || '' }
      //   });

      //   normalized = {
      //     avatar_id: avatarId,
      //     avatar_name: src.avatar_name || src.name || 'Generated Look',
      //     preview_image_url: src.image_url,
      //     image_url: src.image_url,
      //     gender: src.gender || '',
      //     generationId: src.generationId || null
      //   };
      // } else {
        // Library / group avatar: apply avatar_id

        const template_id = src.template_id || src.templateId || (SAMPLE_AVATARS.find(a => String(a.group_id) === String(src.group_id))?.template_id || null)
        let data = {
          avatar_id: avatarId,
          template_id: template_id || null,
          group_id: src.group_id || src.groupId || null,
          gender: src.gender || src.avatar_gender || selectedAvatarGroup?.gender ||'',
        }
        
        await api.put(`/project/${projectId}/avatar`, data, {
          headers: { 'x-access-token': userDetails?.jwtToken || '' }
        });

        normalized = {
          avatar_id: avatarId,
          avatar_name: src.avatar_name || src.name || src.display_name || '',
          preview_image_url: getAvatarImageUrl(src) || src.preview_image_url || src.preview || null,
          gender: src.gender || src.avatar_gender || selectedAvatarGroup?.gender ||'',
          group_id: src.group_id || src.groupId || null,
          template_id: template_id || null
        };
      // }
        console.log('Normalized selected avatar for project:', normalized);
      showMessage('Avatar updated successfully', 'success');
      onSelectAvatar(normalized);
      handleClose();
    } catch (error) {
      console.error('Failed to update project avatar:', error);
      showMessage('Failed to update avatar', 'error');
    }
  };

  const handleClose = () => {
    setNewAvatarPrompt('');
    setAvatarModalTab(0);
    setSelectedAvatarForProject(null);
    setHeygenAvatars(SAMPLE_AVATARS);
    setGroupAvatars([]);
    setGroupView(false);
    setSelectedAvatarGroup(null);
    // stop any polling
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    pollingGenerationRef.current = null;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Select Avatar
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* <Tabs value={avatarModalTab} onChange={(e, v) => setAvatarModalTab(v)} sx={{ mb: 2 }}>
          <Tab label="Select from Library" />
        </Tabs> */}

        {/* Tab 1: Select from Library */}
        {avatarModalTab === 0 && (
          <Box>
            {(loadingAvatars || isFetchingGroupAvatars) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : groupView ? (
              <Box>
                {/* Header actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2, pb: 2 }}>
                  {!showGenerateInline ? (
                    <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
                      <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => setShowGenerateInline(true)}>
                        Generate New Look
                      </Button>
                      {!showGeneratedLooks && (
                        <Button variant="outlined" onClick={fetchGeneratedLooks} disabled={loadingGeneratedLooks}>
                          {loadingGeneratedLooks ? 'Loading...' : 'View Generated Looks'}
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'center' }}>
                      <TextField
                        inputRef={inputRef}
                        fullWidth
                        placeholder="Describe the look to generate..."
                        value={newAvatarPrompt}
                        onChange={(e) => setNewAvatarPrompt(e.target.value)}
                      />
                      <Button variant="contained" onClick={() => handleGenerateNewAvatarLook(extractGroupId(selectedAvatarGroup), selectedAvatarGroup?.template_id)} disabled={generatingAvatar || !newAvatarPrompt.trim()}>
                        {generatingAvatar ? 'Generating...' : 'Generate'}
                      </Button>
                      <Button variant="outlined" onClick={() => { setShowGenerateInline(false); setNewAvatarPrompt(''); }} sx={{ ml: 1 }}>
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Content: either generated looks or group avatars */}
                {showGeneratedLooks ? (
                  <Box>
                    {/* <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                      <Button variant="outlined" onClick={() => setShowGeneratedLooks(false)}>Back to Group</Button>
                    </Box> */}
                    <Grid container spacing={2} sx={{ maxHeight: 500, overflow: 'auto', alignItems: 'stretch' }}>
                      {
                        // flatten each look's image_url_list so each image gets its own card
                        (() => {
                          const sourceItems = (pollImages && pollImages.length) ? [{ image_url_list: pollImages, generationId: pollingGenerationRef.current }] : (generatedLooks || []);
                          const flat = [];
                          sourceItems.forEach((item) => {
                            // const urls = item?.image_url_list || [];
                            console.log('Processing generated look item:', item);
                            const urls = item?.image_key_list || [];
                            const genId = item?.generationId || item?.generation_id || null;
                            const prompt = item?.prompt || '';
                            const status = item?.status || '';
                            urls.forEach((u) => flat.push({ url: u, generationId: genId, prompt, status }));
                          });

                          if (!flat.length) {
                            return (
                              <Grid item xs={12} sx={{ display: 'flex' }}>
                                <Box sx={{ width: '100%', py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                  <InfoOutlinedIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
                                  <Typography variant="h6" gutterBottom>No generated looks found</Typography>
                                  <Typography variant="body2" color="text.secondary">Try generating a new look or check back later.</Typography>
                                </Box>
                              </Grid>
                            );
                          }

                          return flat.map((img, i) => {
                            const thumbSelected = selectedAvatarForProject && selectedAvatarForProject.image_url === img.url;
                            return (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={`gen-img-${i}`} sx={{ display: 'flex' }}>
                                <Card
                                  sx={{
                                    cursor: 'pointer',
                                    border: thumbSelected ? '2px solid #D10000' : '1px solid #ddd',
                                    '&:hover': { boxShadow: 3 },
                                    '&:hover .generated-look-delete, &:focus-within .generated-look-delete': {
                                      opacity: 1,
                                      pointerEvents: 'auto'
                                    },
                                    height: '100%',
                                    width: '100%',
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column'
                                  }}
                                  onClick={() => {
                                    // when a generated image is clicked, ask user to name the look before using it
                                    setPendingLook({ image_url: img.url, generationId: img.generationId, prompt: img.prompt, status: img.status });
                                    setLookName('');
                                    setSelectedAvatarForProject({ image_url: img.url, generationId: img.generationId });
                                    setShowNameDialog(true);
                                  }}
                                >
                                  <IconButton
                                    className="generated-look-delete"
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      zIndex: 2,
                                      bgcolor: 'background.paper',
                                      opacity: 0,
                                      pointerEvents: 'none',
                                      transition: 'opacity 120ms ease'
                                    }}
                                    disabled={!img.generationId || deletingLookKey === img.url}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingDelete({ generationId: img.generationId, imageKeyOrUrl: img.url });
                                      setShowDeleteDialog(true);
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                  <Box sx={{ overflow: 'hidden' }}>
                                    {(() => {
                                      const fullUrl = img.url ? (img.url.startsWith('http') ? img.url : `https://resource2.heygen.ai/${img.url}`) : '/default-avatar.png';
                                      return <ImageCard src={fullUrl} alt={`generated-${i}`} />;
                                    })()}
                                  </Box>
                                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle2" noWrap>{img.prompt || 'Generated Look'}</Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          });
                        })()
                      }
                    </Grid>
                  </Box>
                ) : (
                  <Grid container spacing={2} sx={{ maxHeight: 500, overflow: 'auto', alignItems: 'stretch' }}>
                    {(!Array.isArray(groupAvatars) || groupAvatars.length === 0) ? (
                      <Grid item xs={12} sx={{ display: 'flex' }}>
                        <Box sx={{ width: '100%', py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <InfoOutlinedIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="h6" gutterBottom>No avatar looks found for this group</Typography>
                          <Typography variant="body2" color="text.secondary">Try generating a new look or check back later.</Typography>
                        </Box>
                      </Grid>
                    ) : (
                      [...groupAvatars].map((avatar, idx) => {
                        const avatarId = avatar.id;
                        const imgUrl = getAvatarImageUrl(avatar) || avatar.preview_image_url || '/default-avatar.png';
                        const name = avatar.avatar_name || avatar.name || 'Avatar';
                        const gender = avatar.gender || selectedAvatarGroup?.gender || '';
                        const isSelected = selectedAvatarForProject && (selectedAvatarForProject.avatar_id === avatarId || selectedAvatarForProject.id === avatarId || selectedAvatarForProject._id === avatarId);
                        const isProcessing = isProcessingLikeStatus(avatar?.status);
                        const deletingThis = deletingGroupAvatarId && String(deletingGroupAvatarId) === String(avatarId);

                        const handleCardClick = () => {
                          // When selecting a group avatar, clear any generated images
                          setShowGeneratedLooks(false);
                          setPollImages([]);

                          if (isProcessing) {
                            showMessage('This avatar is still processing. Please wait.', 'info');
                            return;
                          }

                          if (groupView) {
                            setSelectedAvatarForProject(avatar);
                            return;
                          }
                        };

                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={`${avatarId}-${idx}`} sx={{ display: 'flex' }}>
                            <Card
                              sx={{
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                border: isSelected ? '2px solid #D10000' : '1px solid #ddd',
                                '&:hover': isProcessing ? undefined : { boxShadow: 3 },
                                '&:hover .group-avatar-delete, &:focus-within .group-avatar-delete': {
                                  opacity: 1,
                                  pointerEvents: 'auto'
                                },
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                opacity: isProcessing ? 0.7 : 1
                              }}
                              onClick={handleCardClick}
                            >
                              <ImageCard src={imgUrl} alt={name}>
                                {!avatar?.isDefault && (
                                  <IconButton
                                    className="group-avatar-delete"
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      zIndex: 4,
                                      bgcolor: 'background.paper',
                                      opacity: 0,
                                      pointerEvents: 'none',
                                      transition: 'opacity 120ms ease',
                                      ...(isProcessing ? { display: 'none' } : {})
                                    }}
                                    disabled={isProcessing || deletingThis}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingGroupDelete({ photoAvatarId: avatarId, groupId: selectedAvatarGroup?.group_id || selectedAvatarGroup?.id || null });
                                      setShowDeleteGroupDialog(true);
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {isProcessing && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      inset: 0,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: 'rgba(255,255,255,0.65)'
                                    }}
                                  >
                                    <CircularProgress size={28} />
                                    <Typography variant="caption" sx={{ mt: 1 }} color="text.secondary">
                                      Processing...
                                    </Typography>
                                  </Box>
                                )}
                                {deletingThis && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      inset: 0,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: 'rgba(255,255,255,0.65)'
                                    }}
                                  >
                                    <CircularProgress size={28} />
                                    <Typography variant="caption" sx={{ mt: 1 }} color="text.secondary">
                                      Deleting...
                                    </Typography>
                                  </Box>
                                )}
                              </ImageCard>
                              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle2" noWrap>{name}</Typography>
                                <Typography variant="caption" color="text.secondary">{gender}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })
                    )}
                  </Grid>
                )}
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ maxHeight: 500, overflow: 'auto', alignItems: 'stretch' }}>
                {[...heygenAvatars].map((avatar, idx) => {
                  const avatarId = avatar.avatar_id || avatar.id || avatar._id || avatar.character_id;
                  const imgUrl = getAvatarImageUrl(avatar) || avatar.preview_image_url || '/default-avatar.png';
                  const name = avatar.avatar_name || avatar.name || avatar.display_name || 'Avatar';
                  const gender = avatar.gender || '';
                  const isSelected = selectedAvatarForProject && (selectedAvatarForProject.avatar_id === avatarId || selectedAvatarForProject.id === avatarId || selectedAvatarForProject._id === avatarId);

                  const handleCardClick = async () => {
                    setSelectedAvatarGroup(avatar);
                    // Clear generated-looks state when user selects a different avatar/group
                    setShowGeneratedLooks(false);
                    setPollImages([]);
                    const groupId = extractGroupId(avatar);
                    if (groupId) {
                      setIsFetchingGroupAvatars(true);
                      await fetchHeygenAvatars(groupId, avatar);
                      return;
                    }

                    showMessage('Unable to load avatars for this selection', 'info');
                  };

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`${avatarId}-${idx}`} sx={{ display: 'flex' }}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: isSelected ? '2px solid #D10000' : '1px solid #ddd',
                          '&:hover': { boxShadow: 3 },
                          height: '100%',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        onClick={handleCardClick}
                      >
                        <ImageCard src={imgUrl} alt={name} />
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2" noWrap>{name}</Typography>
                          <Typography variant="caption" color="text.secondary">{gender}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

      </DialogContent>

      {/* Confirm delete group avatar dialog */}
      <Dialog
        open={showDeleteGroupDialog}
        onClose={() => {
          if (deletingGroupAvatarId) return;
          setShowDeleteGroupDialog(false);
          setPendingGroupDelete(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete avatar?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this avatar? This will remove it from your saved group.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (deletingGroupAvatarId) return;
              setShowDeleteGroupDialog(false);
              setPendingGroupDelete(null);
            }}
            disabled={!!deletingGroupAvatarId}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!pendingGroupDelete || !!deletingGroupAvatarId}
            onClick={async () => {
              if (!pendingGroupDelete) return;
              await handleDeleteGroupAvatar(pendingGroupDelete);
              setShowDeleteGroupDialog(false);
              setPendingGroupDelete(null);
            }}
          >
            {deletingGroupAvatarId ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => {
          if (deletingLookKey) return;
          setShowDeleteDialog(false);
          setPendingDelete(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete look?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this look? You can’t undo this action.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (deletingLookKey) return;
              setShowDeleteDialog(false);
              setPendingDelete(null);
            }}
            disabled={!!deletingLookKey}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!pendingDelete || !!deletingLookKey}
            onClick={async () => {
              if (!pendingDelete) return;
              await handleSoftDeleteGeneratedLook(pendingDelete);
              setShowDeleteDialog(false);
              setPendingDelete(null);
            }}
          >
            {deletingLookKey ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Naming dialog for selected generated look */}
      <Dialog open={showNameDialog} onClose={() => setShowNameDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Name Generated Look</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>Give a name to this generated look. This will help you identify it later.</Typography>
          <TextField fullWidth label="Look name" value={lookName} onChange={(e) => setLookName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowNameDialog(false); setLookName(''); }}>Cancel</Button>
          <Button variant="contained" disabled={!lookName.trim() || addingLook} onClick={async () => {
            if (!pendingLook) return;
            setAddingLook(true);
            try {
              // prepare add-look payload for backend
              const groupId = extractGroupId(selectedAvatarGroup) || selectedAvatarGroup?.group_id || selectedAvatarGroup?.id || null;
              const templateId = selectedAvatarGroup?.template_id || selectedAvatarGroup?.templateId || null;
              const orgId = getOrgId();
              const addBody = {
                image_url: pendingLook.image_url,
                group_id: groupId,
                template_id: templateId,
                name: lookName.trim(),
                generation_id: pendingLook.generationId || null,
                orgId: orgId || null
              };

              // Call backend to add look to HeyGen group and save to DB
              const addResp = await api.post('/heygen/photo-avatar/group/add-look', addBody, { headers: { 'x-access-token': userDetails?.jwtToken || '' } });

              // Inform user that HeyGen will process the added look shortly
              showMessage('Avatar will be added to group in 1-2 minutes', 'success');

              const saved = addResp?.data?.data || addResp?.data || null;
              const savedItem = Array.isArray(saved) && saved.length ? saved[0] : (Array.isArray(addResp?.data) && addResp.data.length ? addResp.data[0] : null);

              // Fire onSelectAvatar with returned item (or pending look) so UI updates
              const toApply = savedItem || pendingLook;
              // onSelectAvatar({ ...toApply, lookName: lookName.trim() });

              setShowNameDialog(false);
              setPendingLook(null);
              setLookName('');
              // keep modal close behaviour
              handleClose();
            } catch (e) {
              console.error('Failed to add look to group:', e);
              showMessage(e?.response?.data?.message || 'Failed to add look to group', 'error');
            } finally {
              setAddingLook(false);
            }
          }}>Add avatar to group</Button>
        </DialogActions>
      </Dialog>

      <DialogActions>
        <Box display="flex" alignItems="center" width="100%" sx={{ justifyContent: groupView ? 'space-between' : 'flex-end', px: 2 }}>
          {groupView && (
            <Box sx={{ my: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant="contained" onClick={() => {
                setGroupAvatars([]);
                setGroupView(false);
                setSelectedAvatarForProject(null);
                setSelectedAvatarGroup(null);
                setShowGeneratedLooks(false);
              }}>Back to Library</Button>

              {showGeneratedLooks && (
                <Button variant="outlined" onClick={() => setShowGeneratedLooks(false)}>Back to Group</Button>
              )}

              {isFetchingGroupAvatars && <CircularProgress size={20} sx={{ ml: 2 }} />}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleClose}>Cancel</Button>
            {!showGeneratedLooks && (
              <Button
                variant="contained"
                onClick={handleSelectAvatar}
                disabled={!selectedAvatarForProject || isProcessingLikeStatus(selectedAvatarForProject?.status)}
              >
                Select Avatar
              </Button>
            )}
          </Box>
        </Box>

      </DialogActions>
    </Dialog>
  );
};

export default AvatarSelectionModal;
